import * as THREE from 'three'
import { GPUComputationRenderer } from 'three/addons/misc/GPUComputationRenderer.js'
import { MeshSurfaceSampler } from 'three/addons/math/MeshSurfaceSampler.js'
import { params } from '../config/persistence.js'
import { createShapeGeometry } from './geometry.js'
import gpgpuParticlesShader from '../shaders/gpgpu/particles.glsl'
import gpgpuVelocityShader from '../shaders/gpgpu/velocity.glsl'

export const initCompute = (renderer, sampledPositions, particleCount) => {
    const compute = {}
    compute.size = Math.ceil(Math.sqrt(particleCount))
    compute.computation = new GPUComputationRenderer(compute.size, compute.size, renderer)

    const baseParticlesTexture = compute.computation.createTexture()

    for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3
        const i4 = i * 4
        baseParticlesTexture.image.data[i4 + 0] = sampledPositions[i3 + 0]
        baseParticlesTexture.image.data[i4 + 1] = sampledPositions[i3 + 1]
        baseParticlesTexture.image.data[i4 + 2] = sampledPositions[i3 + 2]
        baseParticlesTexture.image.data[i4 + 3] = Math.random()
    }

    let baseMorphTexture
    if (params.morphEnabled) {
        const morphGeometry = createShapeGeometry(params.morphTarget, params)
        const morphMesh = new THREE.Mesh(morphGeometry, new THREE.MeshBasicMaterial())
        const morphSampler = new MeshSurfaceSampler(morphMesh).build()
        const morphPositions = new Float32Array(particleCount * 3)
        const tempPosition = new THREE.Vector3()

        for (let i = 0; i < particleCount; i++) {
            morphSampler.sample(tempPosition)
            morphPositions[i * 3 + 0] = tempPosition.x
            morphPositions[i * 3 + 1] = tempPosition.y
            morphPositions[i * 3 + 2] = tempPosition.z
        }

        baseMorphTexture = compute.computation.createTexture()
        for (let i = 0; i < particleCount; i++) {
            const i3 = i * 3
            const i4 = i * 4
            baseMorphTexture.image.data[i4 + 0] = morphPositions[i3 + 0]
            baseMorphTexture.image.data[i4 + 1] = morphPositions[i3 + 1]
            baseMorphTexture.image.data[i4 + 2] = morphPositions[i3 + 2]
            baseMorphTexture.image.data[i4 + 3] = 0
        }
    } else {
        baseMorphTexture = compute.computation.createTexture()
    }

    const baseVelocityTexture = compute.computation.createTexture()

    compute.particlesVariable = compute.computation.addVariable('uParticles', gpgpuParticlesShader, baseParticlesTexture)
    compute.velocityVariable = compute.computation.addVariable('uVelocity', gpgpuVelocityShader, baseVelocityTexture)

    compute.computation.setVariableDependencies(compute.particlesVariable, [compute.particlesVariable, compute.velocityVariable])
    compute.computation.setVariableDependencies(compute.velocityVariable, [compute.velocityVariable, compute.particlesVariable])

    // Particles uniforms
    const pU = compute.particlesVariable.material.uniforms
    pU.uTime = new THREE.Uniform(0)
    pU.uDeltaTime = new THREE.Uniform(0)
    pU.uBase = new THREE.Uniform(baseParticlesTexture)
    pU.uMouse = new THREE.Uniform(new THREE.Vector3(9999, 9999, 9999))
    pU.uMouseRadius = new THREE.Uniform(params.mouseRadius)
    pU.uMouseAspect = new THREE.Uniform(params.mouseAspect)
    pU.uSuckAmount = new THREE.Uniform(0)
    pU.uMouseTrail = new THREE.Uniform(params.mouseTrail)
    pU.uDecayRate = new THREE.Uniform(params.respawnRate)
    pU.uBase2 = new THREE.Uniform(baseMorphTexture)
    pU.uMorphAmount = new THREE.Uniform(0)

    // Velocity uniforms
    const vU = compute.velocityVariable.material.uniforms
    vU.uTime = new THREE.Uniform(0)
    vU.uDeltaTime = new THREE.Uniform(0)
    vU.uBase = new THREE.Uniform(baseParticlesTexture)
    vU.uMouse = new THREE.Uniform(new THREE.Vector3(9999, 9999, 9999))
    vU.uMouseRadius = new THREE.Uniform(params.mouseRadius)
    vU.uMouseAspect = new THREE.Uniform(params.mouseAspect)
    vU.uSuckAmount = new THREE.Uniform(0)
    vU.uSuckStrength = new THREE.Uniform(params.suckStrength)
    vU.uMouseVelocity = new THREE.Uniform(new THREE.Vector3())
    vU.uWindStrength = new THREE.Uniform(params.windStrength)
    vU.uDamping = new THREE.Uniform(params.damping)
    vU.uTurbulenceStrength = new THREE.Uniform(params.turbulenceStrength)
    vU.uTurbulenceFrequency = new THREE.Uniform(params.turbulenceFrequency)
    vU.uFlowFieldFrequency = new THREE.Uniform(params.flowFrequency)
    vU.uFlowFieldStrength = new THREE.Uniform(params.flowStrength)
    vU.uFlowFieldInfluence = new THREE.Uniform(params.flowInfluence)
    vU.uBase2 = new THREE.Uniform(baseMorphTexture)
    vU.uMorphAmount = new THREE.Uniform(0)
    vU.uMorphStrength = new THREE.Uniform(params.morphEnabled ? params.morphStrength : 0)

    compute.computation.init()

    return { compute, baseParticlesTexture }
}
